// ─── Shared styles — earthy orange palette (matches series) ──────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'1rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.92rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.95rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.95rem' },
  th:           { background:'#a04010', color:'#fff', padding:'11px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.93rem' },
  tdE:          { padding:'11px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'11px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.92rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'1rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.88rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it,i)=><div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th,width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l,v],i)=>(
        <tr key={i}>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.tdL}}>{l}</td>
          <td style={{...(i%2===0?S.tdE:S.tdO),...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─── Arrow marker helper ──────────────────────────────────────────────────────
function Arr({id, color='#c45a10'}) {
  return <marker id={id} markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
    <polygon points="0 0,6 3.5,0 7" fill={color}/>
  </marker>;
}

// RPN colour: green→yellow→orange→red
function rpnColor(rpn) {
  if (rpn <= 50)  return '#5a9a30';
  if (rpn <= 100) return '#c4a010';
  if (rpn <= 200) return '#c45a10';
  return '#8a1010';
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — FMEA Process Flow
// Layout: 7 boxes across 1200px-wide viewBox, sub-labels below, feedback arc at bottom
// ─────────────────────────────────────────────────────────────────────────────
function DiagramProcessFlow() {
  const W=1200, H=360;
  const boxW=128, boxH=80;
  const byTop=70;           // top of all boxes
  const subY=byTop+boxH+18; // y of first sub-label line
  const arcY=300;            // y of feedback arc bottom

  // 7 evenly-spaced box centres across the canvas
  const totalBoxes=7;
  const spacing=(W-boxW)/(totalBoxes-1);
  const steps = [
    { label:'Define\nScope',         sub:'System / boundary\nassembly / process' },
    { label:'Identify\nFunctions',   sub:'What must each\npart / step do?' },
    { label:'Identify\nFailure Modes', sub:'How can each\nfunction fail?' },
    { label:'Identify\nEffects',     sub:'Impact on customer\nor system level' },
    { label:'Identify\nCauses',      sub:'Why does each\nfailure occur?' },
    { label:'Assign\nS · O · D',     sub:'Rate Severity,\nOccurrence, Detection' },
    { label:'Calculate\nRPN',        sub:'RPN = S × O × D\nPrioritise actions' },
  ].map((s,i) => ({ ...s, cx: boxW/2 + i*spacing }));

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>FMEA Process Flow — Seven-Step Methodology</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <Arr id="arPF"/>
          <Arr id="arPFr" color="#7a2e00"/>
        </defs>

        {steps.map((step, i) => {
          const bx = step.cx - boxW/2;
          const fill = i===0?'#d4874a':i===steps.length-1?'#5a1e00':'#c45a10';
          return (
            <g key={i}>
              {/* Box */}
              <rect x={bx} y={byTop} width={boxW} height={boxH} rx={8}
                fill={fill} stroke="#3a0e00" strokeWidth="2"/>
              {/* Step number badge */}
              <circle cx={bx+13} cy={byTop+13} r={10} fill="#fdf0e4" stroke="#3a0e00" strokeWidth="1.5"/>
              <text x={bx+13} y={byTop+17} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">{i+1}</text>
              {/* Label — 2 lines centred in box */}
              {step.label.split('\n').map((l,j) => (
                <text key={j} x={step.cx} y={byTop+36+j*18} textAnchor="middle"
                  fontSize="12" fontWeight="700" fill="#fdf4ec">{l}</text>
              ))}
              {/* Sub-text below box — 2 lines, well clear of box bottom */}
              {step.sub.split('\n').map((s,j) => (
                <text key={j} x={step.cx} y={subY+j*16} textAnchor="middle"
                  fontSize="10" fill="#7a4a28">{s}</text>
              ))}
              {/* Arrow to next box */}
              {i < steps.length-1 && (
                <line
                  x1={bx+boxW+4} y1={byTop+boxH/2}
                  x2={steps[i+1].cx-boxW/2-5} y2={byTop+boxH/2}
                  stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arPF)"/>
              )}
            </g>
          );
        })}

        {/* Feedback arc — from box 7 bottom, sweeps down and back to box 2 bottom */}
        <path
          d={`M ${steps[6].cx} ${byTop+boxH+2}
              Q ${steps[6].cx} ${arcY}
                ${steps[3].cx} ${arcY}
              Q ${steps[1].cx} ${arcY}
                ${steps[1].cx} ${byTop+boxH+2}`}
          fill="none" stroke="#7a2e00" strokeWidth="2.2" strokeDasharray="8,5"
          markerEnd="url(#arPFr)"/>

        {/* Feedback label — centred on the arc, fully below sub-labels */}
        <rect x={steps[3].cx-148} y={arcY-22} width={296} height={28} rx={5}
          fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={steps[3].cx} y={arcY-4} textAnchor="middle"
          fontSize="11" fontWeight="700" fill="#7a2e00">
          Define &amp; verify corrective actions → recalculate RPN
        </text>
      </svg>
      <div style={S.dCaption}>The FMEA loop is iterative — after corrective actions are implemented, Occurrence and Detection ratings are re-assessed to verify RPN reduction meets the target threshold</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — RPN Matrix: Severity vs Occurrence heat map
// Layout: grid 10×10 at cellW=56 → 560px wide. originX=70. Grid right edge=630.
//         Legend starts at x=660. Total W=980 → 350px for legend. No overlap.
// ─────────────────────────────────────────────────────────────────────────────
function DiagramRPNMatrix() {
  const W=980, H=430;
  const cellW=56, cellH=38;
  const originX=70, originY=40;
  const ratings=[1,2,3,4,5,6,7,8,9,10];
  const D=5;

  function cellColor(s, o) {
    const rpn = s*o*D;
    if (rpn <= 40)  return '#6aaa30';
    if (rpn <= 80)  return '#a0c030';
    if (rpn <= 150) return '#c4b820';
    if (rpn <= 250) return '#d08020';
    if (rpn <= 350) return '#c45a10';
    return '#8a1010';
  }
  function textColor(s,o) {
    return s*o*D > 150 ? '#fdf4ec' : '#1a0e06';
  }

  // Grid right edge = originX + 10*cellW = 70 + 560 = 630
  // Legend x = 648 → label at 688 → max text ends ~980 ✓
  const legendX = 648;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>RPN Heat Map — Severity (S) × Occurrence (O) at Detection D=5  ·  RPN = S × O × D</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Y axis label (rotated) */}
        <text x={18} y={originY+cellH*5} textAnchor="middle"
          fontSize="12" fontWeight="700" fill="#7a2e00"
          transform={`rotate(-90,18,${originY+cellH*5})`}>Severity (S)</text>

        {/* X axis label — below grid, well clear */}
        <text x={originX+cellW*5} y={originY+10*cellH+32} textAnchor="middle"
          fontSize="12" fontWeight="700" fill="#7a2e00">Occurrence (O)</text>

        {/* Grid cells */}
        {ratings.map((s,si) =>
          ratings.map((o,oi) => {
            const rpn = s*o*D;
            const cx  = originX + oi*cellW;
            const cy  = originY + (9-si)*cellH;  // S=10 at top
            return (
              <g key={`${si}-${oi}`}>
                <rect x={cx} y={cy} width={cellW-1} height={cellH-1} rx={2}
                  fill={cellColor(s,o)} opacity="0.9"/>
                <text x={cx+cellW/2-0.5} y={cy+cellH/2+5} textAnchor="middle"
                  fontSize="10" fontWeight="600" fill={textColor(s,o)}>{rpn}</text>
              </g>
            );
          })
        )}

        {/* S axis labels (right of rotated label, left of grid) */}
        {ratings.map((s,si) => (
          <text key={si} x={originX-8} y={originY+(9-si)*cellH+cellH/2+5}
            textAnchor="end" fontSize="11" fontWeight="700" fill="#7a2e00">{s}</text>
        ))}

        {/* O axis labels (below grid) */}
        {ratings.map((o,oi) => (
          <text key={oi} x={originX+oi*cellW+cellW/2-0.5} y={originY+10*cellH+16}
            textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">{o}</text>
        ))}

        {/* ── Legend — fully to the right of the grid ── */}
        <text x={legendX} y={originY+10} fontSize="12" fontWeight="700" fill="#7a2e00">Risk Legend (D=5)</text>
        {[
          ['≤ 40',   '#6aaa30', 'Low risk'],
          ['41–80',  '#a0c030', 'Monitor'],
          ['81–150', '#c4b820', 'Moderate — review'],
          ['151–250','#d08020', 'High — action needed'],
          ['251–350','#c45a10', 'Very high — priority action'],
          ['> 350',  '#8a1010', 'Critical — immediate action'],
        ].map(([range, color, label], i) => (
          <g key={i}>
            <rect x={legendX} y={originY+26+i*46} width={30} height={26} rx={4}
              fill={color} opacity="0.9"/>
            <text x={legendX+38} y={originY+26+i*46+11} fontSize="11" fontWeight="700" fill="#7a2e00">
              RPN {range}
            </text>
            <text x={legendX+38} y={originY+26+i*46+24} fontSize="10" fill="#a04010">{label}</text>
          </g>
        ))}

        {/* ── Threshold notes — below legend ── */}
        <text x={legendX} y={originY+26+6*46+12}  fontSize="11" fontWeight="700" fill="#7a2e00">Common thresholds:</text>
        <text x={legendX} y={originY+26+6*46+28}  fontSize="10" fill="#a04010">RPN ≥ 100: document &amp; monitor</text>
        <text x={legendX} y={originY+26+6*46+44}  fontSize="10" fill="#a04010">RPN ≥ 200: corrective action required</text>
        <text x={legendX} y={originY+26+6*46+60}  fontSize="10" fill="#a04010">S ≥ 9 or O ≥ 8: always act</text>
        <text x={legendX} y={originY+26+6*46+75}  fontSize="9"  fill="#7a4a28" fontStyle="italic">Thresholds vary by standard/organisation</text>
      </svg>
      <div style={S.dCaption}>This heat map shows RPN at a fixed Detection rating of 5 — the full 3D RPN space is a 10×10×10 cube; always consider high Severity scores independently of the combined RPN value</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Rating scales: Severity, Occurrence, Detection (side by side)
// ─────────────────────────────────────────────────────────────────────────────
function DiagramRatingScales() {
  const W=980, H=380;

  const severityRows = [
    [10,'Hazardous without warning','Safety risk — regulatory non-compliance'],
    [9, 'Hazardous with warning',   'Safety risk — operator warned'],
    [8, 'Very High',                'Loss of primary function'],
    [7, 'High',                     'Degraded primary function'],
    [6, 'Moderate',                 'Loss of secondary function'],
    [5, 'Low',                      'Degraded secondary function'],
    [4, 'Very Low',                 'Minor loss of fit/finish'],
    [3, 'Minor',                    'Noticed by most customers'],
    [2, 'Very Minor',               'Noticed by discriminating customers'],
    [1, 'None',                     'No discernible effect'],
  ];

  const occurrenceRows = [
    [10,'Almost certain',   '≥ 1 in 2   (Cpk < 0.33)'],
    [9, 'Very High',        '1 in 3     (Cpk < 0.33)'],
    [8, 'High',             '1 in 8     (Cpk ≈ 0.51)'],
    [7, 'Moderately High',  '1 in 20    (Cpk ≈ 0.67)'],
    [6, 'Moderate',         '1 in 80    (Cpk ≈ 0.83)'],
    [5, 'Low',              '1 in 400   (Cpk ≈ 1.00)'],
    [4, 'Relatively Low',   '1 in 2,000 (Cpk ≈ 1.17)'],
    [3, 'Small',            '1 in 15,000(Cpk ≈ 1.33)'],
    [2, 'Remote',           '1 in 150,000(Cpk≈ 1.50)'],
    [1, 'Almost impossible','< 1 in 1.5M(Cpk ≥ 1.67)'],
  ];

  const detectionRows = [
    [10,'Absolute uncertainty','No control exists — cannot detect'],
    [9, 'Very Remote',         'Test/inspection unlikely to detect'],
    [8, 'Remote',              'Visual inspection — low chance'],
    [7, 'Very Low',            'Visual inspection — more thorough'],
    [6, 'Low',                 'Attribute gauging (go/no-go)'],
    [5, 'Moderate',            'Variable gauging — random sampling'],
    [4, 'Moderately High',     'Variable gauging — 100% inspection'],
    [3, 'High',                'Error-proofing — detect most failures'],
    [2, 'Very High',           'Error-proofing — prevents forward pass'],
    [1, 'Almost Certain',      'Prevention — failure cannot occur'],
  ];

  function RatingTable({x, y, w, title, color, rows}) {
    const rowH = 26, hdrH = 28;
    const totalH = hdrH + rows.length * rowH + 10;
    return (
      <g>
        <rect x={x} y={y} width={w} height={totalH} rx={5}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <rect x={x} y={y} width={w} height={hdrH} rx={5}
          fill={color} stroke="none"/>
        <rect x={x} y={y+hdrH-6} width={w} height={6} fill={color} stroke="none"/>
        <text x={x+w/2} y={y+18} textAnchor="middle" fontSize="10" fontWeight="700" fill="#fdf4ec">{title}</text>
        {rows.map(([rating, label, detail], i) => {
          const ry = y + hdrH + i*rowH;
          const bg = i%2===0 ? '#fdf4ec' : '#fdf0e4';
          const barFrac = rating/10;
          // Bar inside rating cell
          return (
            <g key={i}>
              <rect x={x+1} y={ry+1} width={w-2} height={rowH-1}
                fill={bg} rx={i===rows.length-1?'0 0 4 4':'0'}/>
              {/* Rating number cell */}
              <rect x={x+2} y={ry+2} width={26} height={rowH-4} rx={2}
                fill={color} opacity={0.15+barFrac*0.7}/>
              <text x={x+15} y={ry+rowH/2+4} textAnchor="middle"
                fontSize="9" fontWeight="700" fill={color==='#7a2e00'?'#7a2e00':'#5a1e00'}>{rating}</text>
              {/* Label */}
              <text x={x+34} y={ry+rowH/2+4} fontSize="8" fontWeight="700" fill="#7a2e00">{label}</text>
              {/* Detail — right aligned */}
              <text x={x+w-6} y={ry+rowH/2+4} textAnchor="end" fontSize="7.5" fill="#a04010"
                fontFamily="'Courier New',monospace">{detail}</text>
              {/* Divider */}
              {i < rows.length-1 && (
                <line x1={x+2} y1={ry+rowH} x2={x+w-2} y2={ry+rowH}
                  stroke="#e8c89a" strokeWidth="0.8"/>
              )}
            </g>
          );
        })}
      </g>
    );
  }

  const tableW = 298;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Rating Scales — Severity (S) · Occurrence (O) · Detection (D)  [1–10 per AIAG FMEA-4]</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <RatingTable x={8}   y={10} w={tableW} title="SEVERITY  (S) — Impact on Customer" color="#8a1010" rows={severityRows}/>
        <RatingTable x={340} y={10} w={tableW} title="OCCURRENCE (O) — Frequency of Cause" color="#a04010" rows={occurrenceRows}/>
        <RatingTable x={672} y={10} w={tableW} title="DETECTION  (D) — Ability to Detect" color="#c45a10" rows={detectionRows}/>
      </svg>
      <div style={S.dCaption}>These scales follow AIAG FMEA-4 guidelines — specific rating criteria must be customised for each product/process type; a cross-functional team should agree ratings to prevent individual bias</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Sample FMEA worksheet (mechanical bolted joint)
// ─────────────────────────────────────────────────────────────────────────────
function DiagramWorksheet() {
  const W=980, H=420;

  // Columns: Item | Function | Failure Mode | Effect | S | Cause | O | Current Controls | D | RPN | Action | Resp
  const cols = [
    { label:'Item / Function',    x:10,  w:108 },
    { label:'Failure Mode',       x:120, w:116 },
    { label:'Effect(s)',          x:238, w:100 },
    { label:'S',                  x:340, w:30  },
    { label:'Cause(s)',           x:372, w:128 },
    { label:'O',                  x:502, w:30  },
    { label:'Current Controls',   x:534, w:118 },
    { label:'D',                  x:654, w:30  },
    { label:'RPN',                x:686, w:46  },
    { label:'Recommended Action', x:734, w:140 },
    { label:'Resp.',              x:876, w:96  },
  ];

  const rows = [
    // [item, mode, effect, S, cause, O, controls, D, action, resp]
    ['Bolted flange joint\n(pressure vessel)',
     'Bolt under-torque\n(loose joint)',
     'Fluid leak\nplant shutdown',
     8,'Incorrect torque tool\nor procedure',5,'Torque procedure\n(written spec)',6,240,
     'Implement calibrated\ntorque wrench + record',
     'Maint. Eng.\n4 weeks'],
    ['Bolted flange joint\n(pressure vessel)',
     'Bolt over-torque\n(stripped thread)',
     'Joint failure\nsafety risk',
     9,'Wrong torque value\nin work instruction',3,'Torque spec document\n(rev control)',5,135,
     'Add torque value\nto visual job aid',
     'Process Eng.\n2 weeks'],
    ['Gear shaft\n(gearbox output)',
     'Fatigue crack\ninitiation',
     'Shaft fracture\nloss of drive',
     10,'Stress concentration\nat keyway radius',4,'FEA analysis\nat design stage',7,280,
     'Increase keyway\nradius per DIN 6885A',
     'Design Eng.\n6 weeks'],
    ['Gear shaft\n(gearbox output)',
     'Fretting corrosion\nat bearing seat',
     'Bearing failure\nvibration noise',
     7,'Insufficient\ninterference fit',5,'CMM inspection\nof bearing bore',4,140,
     'Revise fit tolerance\nto H7/p6',
     'Design Eng.\n3 weeks'],
    ['Hydraulic actuator\n(cylinder bore)',
     'Seal wear\n& leakage',
     'Loss of pressure\nposition drift',
     8,'Contamination in\nhydraulic fluid',6,'100% pressure test\nat assembly',5,240,
     'Add inline filter\n+ fluid spec check',
     'Maint. Eng.\n3 weeks'],
    ['Weld joint\n(structural frame)',
     'Incomplete fusion\n(cold weld)',
     'Joint fracture\nunder load',
     9,'Incorrect weld\nparameters',4,'Visual inspection\npost-weld',8,288,
     'Add UT inspection\n+ weld procedure WPS',
     'QA Engineer\n4 weeks'],
  ];

  const hdrH = 36, rowH = 54, topY = 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Sample FMEA Worksheet — Mechanical Components (Bolted Joint · Gear Shaft · Hydraulic Actuator · Weld)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Header row */}
        {cols.map((col, ci) => (
          <g key={ci}>
            <rect x={col.x} y={topY} width={col.w} height={hdrH}
              fill="#a04010" stroke="#7a2e00" strokeWidth="0.8"/>
            {col.label.split(' ').reduce((lines, word) => {
              // Simple word-wrap into 2 lines
              if (lines.length === 0) return [word];
              const last = lines[lines.length-1];
              if ((last+' '+word).length <= 13) {
                return [...lines.slice(0,-1), last+' '+word];
              }
              return [...lines, word];
            }, []).map((line,j)=>(
              <text key={j} x={col.x+col.w/2} y={topY+14+j*12} textAnchor="middle"
                fontSize="7.5" fontWeight="700" fill="#fdf4ec">{line}</text>
            ))}
          </g>
        ))}

        {/* Data rows */}
        {rows.map((row, ri) => {
          const [item,mode,effect,S,cause,O,controls,D,action,resp] = row;
          const RPN = S*O*D;
          const ry = topY + hdrH + ri * rowH;
          const bg = ri%2===0 ? '#fdf4ec' : '#fdf0e4';
          const rpnFill = RPN>=250?'#8a1010':RPN>=150?'#c45a10':RPN>=80?'#c4a010':'#5a9a30';

          const cells = [item,mode,effect,S,cause,O,controls,D,RPN,action,resp].map(v=>String(v));

          return (
            <g key={ri}>
              {cols.map((col, ci) => {
                const val = cells[ci];
                const isRPN = ci===8;
                const isSOD = [3,5,7].includes(ci);
                const cellBg = isRPN ? rpnFill : bg;
                const textFill = isRPN ? '#fdf4ec' : isSOD ? '#7a2e00' : '#1a0e06';
                return (
                  <g key={ci}>
                    <rect x={col.x} y={ry} width={col.w} height={rowH}
                      fill={cellBg} stroke="#e8c89a" strokeWidth="0.8"/>
                    {val.split('\n').map((line, j) => (
                      <text key={j} x={col.x+col.w/2} y={ry+16+j*13} textAnchor="middle"
                        fontSize={isSOD||isRPN?9:7.2}
                        fontWeight={isSOD||isRPN?'700':'400'}
                        fontFamily={isSOD||isRPN?'Georgia':"'Courier New',monospace"}
                        fill={textFill}>{line}</text>
                    ))}
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* RPN colour legend */}
        {[['< 80','#5a9a30'],['80–149','#c4a010'],['150–249','#c45a10'],['≥ 250','#8a1010']].map(([label,color],i)=>(
          <g key={i}>
            <rect x={10+i*120} y={topY+hdrH+rows.length*rowH+10} width={16} height={12} rx={2} fill={color}/>
            <text x={30+i*120} y={topY+hdrH+rows.length*rowH+21} fontSize="8" fill="#7a2e00">RPN {label}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>RPN colour-coding: green (&lt;80) monitor · amber (80–149) review · orange (150–249) corrective action · red (≥250) priority/immediate action — thresholds should be set per project quality plan</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Failure mode taxonomy (function → failure modes → effects tree)
// ─────────────────────────────────────────────────────────────────────────────
function DiagramFailureTree() {
  const W=980, H=360;

  // Tree structure for a hydraulic pump
  const root  = { label:'Hydraulic Pump\n(Function: deliver 200 bar\nat 40 L/min)', x:100, y:160 };

  const modes = [
    { label:'No Flow\n(total failure)',    x:320, y:60  },
    { label:'Reduced Flow\n(<40 L/min)',   x:320, y:160 },
    { label:'Excessive Noise\n& Vibration',x:320, y:260 },
  ];

  const causes = [
    // for No Flow
    { label:'Seized pump\nbearings',        x:510, y:30,  parent:0 },
    { label:'Drive coupling\nfailure',       x:510, y:90,  parent:0 },
    // for Reduced Flow
    { label:'Worn pump\nvanes/gears',        x:510, y:145, parent:1 },
    { label:'Internal\nleakage/bypass',      x:510, y:195, parent:1 },
    // for Noise
    { label:'Cavitation\n(low inlet press)', x:510, y:245, parent:2 },
    { label:'Air ingestion\nin fluid',        x:510, y:295, parent:2 },
  ];

  const effects = [
    // for causes 0,1 (no flow)
    { label:'System pressure\nloss — machine stop', x:720, y:30,  parent:0, S:9 },
    { label:'Drive train\ndamage',                  x:720, y:90,  parent:1, S:7 },
    // for causes 2,3 (reduced flow)
    { label:'Slow actuator\nresponse',              x:720, y:145, parent:2, S:6 },
    { label:'Cycle time\nexceeded',                 x:720, y:195, parent:3, S:5 },
    // for causes 4,5 (noise)
    { label:'Pump wear\nshortened life',            x:720, y:245, parent:4, S:7 },
    { label:'Fluid degradation\n& contamination',  x:720, y:295, parent:5, S:6 },
  ];

  const bw=154, bh=38;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Failure Mode Taxonomy — Function → Failure Modes → Causes → Effects (Hydraulic Pump Example)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs><Arr id="arFT"/></defs>

        {/* Column headers */}
        {[
          [100,  'FUNCTION'],
          [320,  'FAILURE MODES'],
          [510,  'CAUSES'],
          [720,  'EFFECTS  (with S)'],
        ].map(([cx, label],i)=>(
          <text key={i} x={cx+bw/2} y={18} textAnchor="middle"
            fontSize="8.5" fontWeight="700" fill="#7a2e00"
            style={{textTransform:'uppercase'}}>{label}</text>
        ))}
        {[215,405,605].map((x,i)=>(
          <line key={i} x1={x} y1={22} x2={x} y2={H-10}
            stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
        ))}

        {/* Root node */}
        <rect x={root.x} y={root.y-bh/2} width={bw} height={bh+14} rx={6}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="2"/>
        {root.label.split('\n').map((l,j)=>(
          <text key={j} x={root.x+bw/2} y={root.y-bh/2+16+j*13} textAnchor="middle"
            fontSize="7.5" fontWeight="700" fill="#fdf4ec">{l}</text>
        ))}

        {/* Failure mode nodes */}
        {modes.map((m,i)=>(
          <g key={i}>
            {/* Connector root→mode */}
            <line x1={root.x+bw} y1={root.y+bh/4}
              x2={m.x} y2={m.y}
              stroke="#c45a10" strokeWidth="1.5" markerEnd="url(#arFT)"/>
            <rect x={m.x} y={m.y-bh/2} width={bw} height={bh+2} rx={5}
              fill="#c45a10" stroke="#7a2e00" strokeWidth="1.8"/>
            {m.label.split('\n').map((l,j)=>(
              <text key={j} x={m.x+bw/2} y={m.y-bh/2+14+j*13} textAnchor="middle"
                fontSize="7.5" fontWeight="700" fill="#fdf4ec">{l}</text>
            ))}
          </g>
        ))}

        {/* Cause nodes */}
        {causes.map((c,i)=>(
          <g key={i}>
            <line x1={modes[c.parent].x+bw} y1={modes[c.parent].y}
              x2={c.x} y2={c.y}
              stroke="#a04010" strokeWidth="1.3" markerEnd="url(#arFT)"/>
            <rect x={c.x} y={c.y-bh/2} width={bw} height={bh+2} rx={4}
              fill="#e8b07a" stroke="#7a2e00" strokeWidth="1.5"/>
            {c.label.split('\n').map((l,j)=>(
              <text key={j} x={c.x+bw/2} y={c.y-bh/2+14+j*13} textAnchor="middle"
                fontSize="7.5" fill="#1a0e06">{l}</text>
            ))}
          </g>
        ))}

        {/* Effect nodes */}
        {effects.map((e,i)=>(
          <g key={i}>
            <line x1={causes[e.parent].x+bw} y1={causes[e.parent].y}
              x2={e.x} y2={e.y}
              stroke="#7a2e00" strokeWidth="1.3" markerEnd="url(#arFT)"/>
            <rect x={e.x} y={e.y-bh/2} width={bw} height={bh+2} rx={4}
              fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
            {e.label.split('\n').map((l,j)=>(
              <text key={j} x={e.x+bw/2} y={e.y-bh/2+14+j*13} textAnchor="middle"
                fontSize="7.5" fill="#1a0e06">{l}</text>
            ))}
            {/* S badge */}
            <rect x={e.x+bw-24} y={e.y-bh/2-1} width={24} height={16} rx={3}
              fill={e.S>=9?'#8a1010':e.S>=7?'#c45a10':'#c4a010'}/>
            <text x={e.x+bw-12} y={e.y-bh/2+11} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#fdf4ec">S={e.S}</text>
          </g>
        ))}

        {/* Legend for arrow directions */}
        <rect x={8} y={H-28} width={380} height={20} rx={3} fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1"/>
        <text x={14} y={H-14} fontSize="8" fill="#7a2e00" fontWeight="700">Reading direction:</text>
        <text x={110} y={H-14} fontSize="8" fill="#a04010">Function → Failure Mode → Cause → Effect   |   S badge = Severity rating</text>
      </svg>
      <div style={S.dCaption}>A single function can have multiple failure modes; each failure mode can have multiple causes and effects — FMEA systematically maps every combination to ensure none are overlooked</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — DFMEA vs PFMEA comparison + action priority matrix
// ─────────────────────────────────────────────────────────────────────────────
function DiagramTypesComparison() {
  const W=980, H=320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>DFMEA vs PFMEA vs FMEA-MSR — Types, Scope, and Action Priority Matrix</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs><Arr id="arTC"/></defs>

        {/* ─── Left: 3 type cards ─── */}
        {[
          { label:'Design FMEA\n(DFMEA)', color:'#8a1010',
            lines:['Scope: design intent — what could fail?',
                   'When: concept through design release',
                   'Team: design, reliability, manufacturing',
                   'Focus: component & sub-system functions',
                   'Output: design changes, tolerance review,',
                   '  material selection, DVP&R test additions',
                   'Standard: AIAG-VDA DFMEA (2019)'] },
          { label:'Process FMEA\n(PFMEA)', color:'#a04010',
            lines:['Scope: manufacturing & assembly process',
                   'When: process design through SOP release',
                   'Team: process, quality, maintenance eng.',
                   'Focus: process steps that can produce defects',
                   'Output: control plan, mistake-proofing (poka-yoke),',
                   '  inspection additions, operator instructions',
                   'Standard: AIAG-VDA PFMEA (2019)'] },
          { label:'FMEA-MSR\n(Monitoring/Supplement)', color:'#c45a10',
            lines:['Scope: safety monitoring during field operation',
                   'When: after DFMEA, for safety-relevant items',
                   'Team: safety, systems, design engineers',
                   'Focus: diagnostic coverage of E/E systems',
                   'Output: diagnostic test intervals, fault trees,',
                   '  FMEDA, ASIL classification support',
                   'Standard: AIAG-VDA MSR (2019) / ISO 26262'] },
        ].map((card, i) => {
          const x = 10 + i*215, y = 14, w = 208, h = 278;
          return (
            <g key={i}>
              <rect x={x} y={y} width={w} height={h} rx={6}
                fill="#fdf0e4" stroke={card.color} strokeWidth="2"/>
              <rect x={x} y={y} width={w} height={36} rx={6}
                fill={card.color} stroke="none"/>
              <rect x={x} y={y+30} width={w} height={6} fill={card.color} stroke="none"/>
              {card.label.split('\n').map((l,j)=>(
                <text key={j} x={x+w/2} y={y+15+j*14} textAnchor="middle"
                  fontSize="10" fontWeight="700" fill="#fdf4ec">{l}</text>
              ))}
              {card.lines.map((line,j)=>(
                <text key={j} x={x+8} y={y+52+j*30} fontSize="8"
                  fill="#1a0e06">{line}</text>
              ))}
            </g>
          );
        })}

        {/* ─── Right: Action Priority Matrix (AIAG-VDA 2019) ─── */}
        {(()=>{
          const px=660, py=14, pw=310, ph=278;
          const apRows=[
            ['H','High',  'Immediate action mandatory\nTeam leader must escalate to management'],
            ['M','Medium','Action is strongly recommended\nDocument rationale if no action taken'],
            ['L','Low',   'Action at team discretion\nDocument current controls adequacy'],
          ];
          const colors=['#8a1010','#c45a10','#5a9a30'];
          return <g>
            <rect x={px} y={py} width={pw} height={ph} rx={6}
              fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.8"/>
            <rect x={px} y={py} width={pw} height={30} rx={6}
              fill="#a04010" stroke="none"/>
            <rect x={px} y={py+24} width={pw} height={6} fill="#a04010" stroke="none"/>
            <text x={px+pw/2} y={py+18} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#fdf4ec">
              Action Priority (AP) — AIAG-VDA 2019
            </text>
            <text x={px+8} y={py+46} fontSize="8" fill="#7a2e00" fontWeight="700">Replaces standalone RPN threshold in AIAG-VDA:</text>
            <text x={px+8} y={py+58} fontSize="8" fill="#a04010">AP considers S, O, D individually (not just product)</text>
            {apRows.map(([code,label,desc],i)=>{
              const ry=py+70+i*62;
              return <g key={i}>
                <rect x={px+8} y={ry} width={pw-16} height={56} rx={4}
                  fill={colors[i]} opacity={0.12}
                  stroke={colors[i]} strokeWidth="1.5"/>
                <rect x={px+8} y={ry} width={30} height={56} rx={4}
                  fill={colors[i]} opacity={0.7}/>
                <text x={px+23} y={ry+34} textAnchor="middle" fontSize="18" fontWeight="700"
                  fill="#fdf4ec">{code}</text>
                <text x={px+46} y={ry+16} fontSize="9" fontWeight="700" fill={colors[i]}>{label}</text>
                {desc.split('\n').map((d,j)=>(
                  <text key={j} x={px+46} y={ry+30+j*13} fontSize="8" fill="#1a0e06">{d}</text>
                ))}
              </g>;
            })}
            <text x={px+8} y={py+268} fontSize="7.5" fill="#7a4a28" fontStyle="italic">
              AP lookup table: S(1–10) × O(1–10) × D(1–10) → H/M/L in AIAG-VDA tables
            </text>
          </g>;
        })()}
      </svg>
      <div style={S.dCaption}>The 2019 AIAG-VDA standard replaced the single RPN threshold with the Action Priority (AP) system — AP=H must always be acted upon regardless of individual S, O, D values</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — RPN before/after waterfall: corrective actions impact
// ─────────────────────────────────────────────────────────────────────────────
function DiagramRPNWaterfall() {
  const W=980, H=340;

  const items = [
    { label:'Bolt under-torque',    before:{S:8,O:5,D:6}, after:{S:8,O:2,D:3},
      action:'Calibrated torque wrench\n+ mandatory record' },
    { label:'Fatigue crack',        before:{S:10,O:4,D:7}, after:{S:10,O:2,D:4},
      action:'Increase keyway radius\n+ fatigue life FEA' },
    { label:'Hydraulic seal wear',  before:{S:8,O:6,D:5}, after:{S:8,O:3,D:3},
      action:'Add inline filter\n+ fluid spec check' },
    { label:'Incomplete weld',      before:{S:9,O:4,D:8}, after:{S:9,O:2,D:3},
      action:'UT inspection +\nqualified welders WPS' },
    { label:'Shaft fretting',       before:{S:7,O:5,D:4}, after:{S:7,O:2,D:2},
      action:'Revise interference\nfit tolerance H7/p6' },
  ];

  const baseY=240, maxBarH=180, barW=80, gap=24;
  const startX=60;
  const maxRPN=500;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>RPN Before vs. After Corrective Actions — Waterfall Chart</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs><Arr id="arWF"/></defs>

        {/* Y axis */}
        <line x1={startX-4} y1={baseY-maxBarH-10} x2={startX-4} y2={baseY+4}
          stroke="#7a2e00" strokeWidth="1.8"/>
        <line x1={startX-4} y1={baseY} x2={W-20} y2={baseY}
          stroke="#7a2e00" strokeWidth="1.8"/>
        {[0,100,200,300,400,500].map(v=>{
          const y=baseY-(v/maxRPN)*maxBarH;
          return <g key={v}>
            <line x1={startX-8} y1={y} x2={W-20} y2={y}
              stroke="#e8b07a" strokeWidth="1" strokeDasharray="4,3"/>
            <text x={startX-12} y={y+4} textAnchor="end" fontSize="8.5" fill="#7a4a28">{v}</text>
          </g>;
        })}
        <text x={startX-40} y={baseY-maxBarH/2} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00"
          transform={`rotate(-90,${startX-40},${baseY-maxBarH/2})`}>RPN Value</text>

        {/* Action threshold line */}
        <line x1={startX-4} y1={baseY-(200/maxRPN)*maxBarH} x2={W-20} y2={baseY-(200/maxRPN)*maxBarH}
          stroke="#8a1010" strokeWidth="1.8" strokeDasharray="6,3"/>
        <text x={W-18} y={baseY-(200/maxRPN)*maxBarH-5} textAnchor="end" fontSize="8" fill="#8a1010" fontWeight="700">
          Action threshold (RPN 200)
        </text>

        {items.map((item, i) => {
          const rpnBefore = item.before.S * item.before.O * item.before.D;
          const rpnAfter  = item.after.S  * item.after.O  * item.after.D;
          const reduction = rpnBefore - rpnAfter;
          const bx = startX + i*(barW+gap);
          const beforeH = (rpnBefore/maxRPN)*maxBarH;
          const afterH  = (rpnAfter/maxRPN)*maxBarH;
          const halfW   = barW/2-2;

          return (
            <g key={i}>
              {/* Before bar (left half) */}
              <rect x={bx} y={baseY-beforeH} width={halfW} height={beforeH} rx="3 0 0 3"
                fill="#c45a10" stroke="#7a2e00" strokeWidth="1.2" opacity="0.88"/>
              {/* After bar (right half) */}
              <rect x={bx+halfW+2} y={baseY-afterH} width={halfW} height={afterH} rx="0 3 3 0"
                fill="#5a9a30" stroke="#5a7820" strokeWidth="1.2" opacity="0.88"/>
              {/* RPN values */}
              <text x={bx+halfW/2} y={baseY-beforeH-5} textAnchor="middle"
                fontSize="8.5" fontWeight="700" fill="#c45a10">{rpnBefore}</text>
              <text x={bx+halfW+2+halfW/2} y={baseY-afterH-5} textAnchor="middle"
                fontSize="8.5" fontWeight="700" fill="#5a9a30">{rpnAfter}</text>
              {/* Reduction arrow */}
              <line x1={bx+barW/2} y1={baseY-beforeH-14} x2={bx+barW/2} y2={baseY-afterH-14}
                stroke="#7a2e00" strokeWidth="1.5" markerEnd="url(#arWF)"/>
              <text x={bx+barW/2} y={baseY-Math.max(beforeH,afterH)-22} textAnchor="middle"
                fontSize="7.5" fontWeight="700" fill="#7a2e00">−{reduction}</text>
              {/* Item label */}
              <text x={bx+barW/2} y={baseY+14} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a2e00">
                {item.label.split(' ')[0]}
              </text>
              <text x={bx+barW/2} y={baseY+26} textAnchor="middle" fontSize="7.5" fill="#7a2e00">
                {item.label.split(' ').slice(1).join(' ')}
              </text>
              {/* Action tooltip below */}
              {item.action.split('\n').map((l,j)=>(
                <text key={j} x={bx+barW/2} y={baseY+44+j*11} textAnchor="middle" fontSize="6.8" fill="#a04010">{l}</text>
              ))}
            </g>
          );
        })}

        {/* Legend */}
        <rect x={W-180} y={20} width={14} height={14} rx={2} fill="#c45a10" opacity="0.88"/>
        <text x={W-162} y={31} fontSize="9" fill="#c45a10" fontWeight="700">Before action</text>
        <rect x={W-180} y={40} width={14} height={14} rx={2} fill="#5a9a30" opacity="0.88"/>
        <text x={W-162} y={51} fontSize="9" fill="#5a9a30" fontWeight="700">After action</text>
      </svg>
      <div style={S.dCaption}>Corrective actions primarily target Occurrence (better process control) and Detection (improved inspection/error-proofing) — Severity can only be reduced by redesigning the system function itself</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 8 — FMEA in the V-model and product development timeline
// ─────────────────────────────────────────────────────────────────────────────
function DiagramVModel() {
  const W=980, H=340;

  const leftSteps = [
    { label:'System Requirements', x:60,  y:50  },
    { label:'System Design',        x:120, y:100 },
    { label:'Sub-system Design',    x:180, y:150 },
    { label:'Component Design',     x:240, y:200 },
    { label:'Coding / Detail Mfg',  x:300, y:250 },
  ];
  const rightSteps = [
    { label:'Component Test',       x:680, y:200 },
    { label:'Sub-system Integration',x:740,y:150 },
    { label:'System Integration',   x:800, y:100 },
    { label:'Acceptance / SOP',     x:860, y:50  },
  ];
  const bottomStep = { label:'Prototype Build\n& Integration', x:490, y:290 };

  // FMEA annotations (which FMEA applies at which V-model node)
  const annotations = [
    { nodeX:120, nodeY:100, label:'System DFMEA\nSystem-level failure modes', side:'left' },
    { nodeX:180, nodeY:150, label:'Sub-system DFMEA\nInterface failure modes',   side:'left' },
    { nodeX:240, nodeY:200, label:'Component DFMEA\nDetail design failure modes', side:'left' },
    { nodeX:300, nodeY:250, label:'PFMEA initiated\nProcess planning begins',    side:'left' },
    { nodeX:680, nodeY:200, label:'PFMEA finalised\nControl plan issued',         side:'right' },
    { nodeX:800, nodeY:100, label:'FMEA-MSR added\nDiagnostic coverage check',   side:'right' },
    { nodeX:860, nodeY:50,  label:'All FMEAs reviewed\nLessons learned captured', side:'right' },
  ];

  const nodeR = 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>FMEA in the V-Model — When Each FMEA Type is Initiated and Updated</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs><Arr id="arVM"/></defs>

        {/* V-model left arm */}
        {leftSteps.map((s,i)=>{
          const next = i<leftSteps.length-1 ? leftSteps[i+1] : bottomStep;
          return <line key={i} x1={s.x} y1={s.y} x2={next.x} y2={next.y}
            stroke="#c45a10" strokeWidth="3" strokeLinecap="round"/>;
        })}
        {/* V-model bottom → right */}
        <line x1={bottomStep.x} y1={bottomStep.y} x2={rightSteps[0].x} y2={rightSteps[0].y}
          stroke="#c45a10" strokeWidth="3" strokeLinecap="round"/>
        {/* V-model right arm */}
        {rightSteps.map((s,i)=>{
          const next = i<rightSteps.length-1 ? rightSteps[i+1] : null;
          if (!next) return null;
          return <line key={i} x1={s.x} y1={s.y} x2={next.x} y2={next.y}
            stroke="#c45a10" strokeWidth="3" strokeLinecap="round"/>;
        })}

        {/* Horizontal verification arrows */}
        {[
          [leftSteps[1], rightSteps[2]],
          [leftSteps[2], rightSteps[1]],
          [leftSteps[3], rightSteps[0]],
        ].map(([l,r],i)=>(
          <line key={i} x1={l.x+nodeR+4} y1={l.y} x2={r.x-nodeR-4} y2={r.y}
            stroke="#7a2e00" strokeWidth="1.2" strokeDasharray="6,4"
            markerEnd="url(#arVM)"/>
        ))}

        {/* Nodes */}
        {[...leftSteps, bottomStep, ...rightSteps].map((s,i)=>{
          const isBottom = i === leftSteps.length;
          const fill = isBottom ? '#d4874a' : i < leftSteps.length ? '#c45a10' : '#7a2e00';
          return (
            <g key={i}>
              <circle cx={s.x} cy={s.y} r={nodeR} fill={fill} stroke="#5a1e00" strokeWidth="1.5"/>
              {/* Labels: left arm labels go left, right arm go right */}
              {i < leftSteps.length && (
                <text x={s.x-nodeR-6} y={s.y+4} textAnchor="end" fontSize="8" fontWeight="700" fill="#7a2e00">{s.label}</text>
              )}
              {i > leftSteps.length && (
                <text x={s.x+nodeR+6} y={s.y+4} textAnchor="start" fontSize="8" fontWeight="700" fill="#7a2e00">{s.label}</text>
              )}
              {isBottom && s.label.split('\n').map((l,j)=>(
                <text key={j} x={s.x} y={s.y+nodeR+14+j*12} textAnchor="middle"
                  fontSize="8" fontWeight="700" fill="#7a2e00">{l}</text>
              ))}
            </g>
          );
        })}

        {/* FMEA annotation boxes */}
        {annotations.map((a,i)=>{
          const bw=170, bh=28;
          const bx = a.side==='left' ? a.nodeX+20 : a.nodeX - bw - 20;
          const by = a.nodeY - bh/2;
          return (
            <g key={i}>
              <line x1={a.nodeX+(a.side==='left'?nodeR:-nodeR)} y1={a.nodeY}
                x2={a.side==='left'?bx:bx+bw} y2={a.nodeY}
                stroke="#a04010" strokeWidth="1.2" strokeDasharray="4,2"/>
              <rect x={bx} y={by} width={bw} height={bh} rx={3}
                fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
              {a.label.split('\n').map((l,j)=>(
                <text key={j} x={bx+4} y={by+13+j*12} fontSize="7.2"
                  fontWeight={j===0?'700':'400'} fill={j===0?'#7a2e00':'#a04010'}>{l}</text>
              ))}
            </g>
          );
        })}

        {/* Timeline bar */}
        <rect x={50} y={H-22} width={W-100} height={14} rx={4}
          fill="#fdf0e4" stroke="#e8b07a" strokeWidth="1.2"/>
        <rect x={50} y={H-22} width={(W-100)*0.45} height={14} rx={4}
          fill="#c45a10" opacity="0.3" stroke="none"/>
        <text x={50+(W-100)*0.225} y={H-11} textAnchor="middle" fontSize="7.5" fill="#7a2e00" fontWeight="700">
          DFMEA active
        </text>
        <rect x={50+(W-100)*0.30} y={H-22} width={(W-100)*0.55} height={14} rx={4}
          fill="#a04010" opacity="0.25" stroke="none"/>
        <text x={50+(W-100)*0.58} y={H-11} textAnchor="middle" fontSize="7.5" fill="#7a2e00" fontWeight="700">
          PFMEA active
        </text>
      </svg>
      <div style={S.dCaption}>FMEAs should be living documents — initiated early (concept stage) and updated at every design review; a first-issue FMEA only days before launch is a significant quality risk</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DIAGRAM 9 — Criticality matrix (MIL-STD-1629A style)
// ─────────────────────────────────────────────────────────────────────────────
function DiagramCriticalityMatrix() {
  const W=980, H=340;

  // MIL-STD-1629A severity categories vs probability levels
  const severityCategories = [
    { code:'I',   label:'Catastrophic', desc:'Death or system loss' },
    { code:'II',  label:'Critical',     desc:'Severe injury or major damage' },
    { code:'III', label:'Marginal',     desc:'Minor injury or degradation' },
    { code:'IV',  label:'Negligible',   desc:'No injury, minor damage' },
  ];
  const probLevels = [
    { code:'E', label:'Frequent',   prob:'> 0.20 per unit hr' },
    { code:'D', label:'Probable',   prob:'0.10 – 0.20' },
    { code:'C', label:'Occasional', prob:'0.01 – 0.10' },
    { code:'B', label:'Remote',     prob:'0.001 – 0.01' },
    { code:'A', label:'Improbable', prob:'< 0.001' },
  ];

  // Risk region: [prob index][sev index] → color
  const risk = [
    // E (most frequent)
    ['#8a1010','#8a1010','#c45a10','#c4a010'],
    // D
    ['#8a1010','#c45a10','#c45a10','#c4a010'],
    // C
    ['#8a1010','#c45a10','#c4a010','#5a9a30'],
    // B
    ['#c45a10','#c4a010','#c4a010','#5a9a30'],
    // A (improbable)
    ['#c4a010','#c4a010','#5a9a30','#5a9a30'],
  ];

  const cellW=110, cellH=44, originX=220, originY=50;

  // Example data points
  const dataPoints = [
    { label:'Bolt fracture', pi:0, si:0, note:'Redesign required' },
    { label:'Seal leak',     pi:2, si:1, note:'Action needed' },
    { label:'Wear noise',    pi:2, si:2, note:'Monitor' },
    { label:'Surface rust',  pi:3, si:3, note:'Acceptable' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Criticality Matrix — MIL-STD-1629A Style: Severity Category × Probability Level</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Severity column headers */}
        {severityCategories.map((sc,i)=>(
          <g key={i}>
            <rect x={originX+i*cellW} y={originY-40} width={cellW-1} height={38} rx={2}
              fill="#a04010" stroke="#7a2e00" strokeWidth="0.8"/>
            <text x={originX+i*cellW+cellW/2} y={originY-26} textAnchor="middle"
              fontSize="8.5" fontWeight="700" fill="#fdf4ec">Cat. {sc.code}: {sc.label}</text>
            <text x={originX+i*cellW+cellW/2} y={originY-12} textAnchor="middle"
              fontSize="7" fill="#fdf0e4">{sc.desc}</text>
          </g>
        ))}

        {/* Probability row headers */}
        {probLevels.map((pl,i)=>(
          <g key={i}>
            <rect x={10} y={originY+i*cellH} width={originX-14} height={cellH-1} rx={2}
              fill="#d4874a" stroke="#7a2e00" strokeWidth="0.8"/>
            <text x={14} y={originY+i*cellH+14} fontSize="8.5" fontWeight="700" fill="#5a1e00">
              {pl.code}: {pl.label}
            </text>
            <text x={14} y={originY+i*cellH+26} fontSize="7.5" fill="#7a4a28"
              fontFamily="'Courier New',monospace">{pl.prob}</text>
          </g>
        ))}

        {/* Risk cells */}
        {probLevels.map((pl,pi)=>
          severityCategories.map((sc,si)=>{
            const cx = originX+si*cellW, cy = originY+pi*cellH;
            const color = risk[pi][si];
            return (
              <rect key={`${pi}-${si}`} x={cx} y={cy} width={cellW-1} height={cellH-1} rx={2}
                fill={color} opacity="0.75" stroke="#fdf4ec" strokeWidth="0.5"/>
            );
          })
        )}

        {/* Data points */}
        {dataPoints.map((dp,i)=>{
          const cx = originX+dp.si*cellW+cellW/2;
          const cy = originY+dp.pi*cellH+cellH/2;
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r={12} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
              <text x={cx} y={cy+4} textAnchor="middle" fontSize="7" fontWeight="700" fill="#7a2e00">{i+1}</text>
            </g>
          );
        })}

        {/* Legend */}
        {[
          ['#8a1010','Unacceptable — redesign mandatory'],
          ['#c45a10','High risk — corrective action required'],
          ['#c4a010','Moderate risk — monitor & review'],
          ['#5a9a30','Acceptable with review'],
        ].map(([color,label],i)=>(
          <g key={i}>
            <rect x={700} y={50+i*36} width={20} height={20} rx={3} fill={color} opacity="0.75"/>
            <text x={726} y={64+i*36} fontSize="8.5" fill="#7a2e00">{label}</text>
          </g>
        ))}
        <text x={700} y={36} fontSize="9.5" fontWeight="700" fill="#7a2e00">Risk Region</text>

        {/* Numbered legend */}
        {dataPoints.map((dp,i)=>(
          <g key={i}>
            <circle cx={714} cy={204+i*28} r={10} fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.2"/>
            <text x={714} y={208+i*28} textAnchor="middle" fontSize="7" fontWeight="700" fill="#7a2e00">{i+1}</text>
            <text x={730} y={208+i*28} fontSize="8" fill="#7a2e00" fontWeight="700">{dp.label}</text>
            <text x={730} y={220+i*28} fontSize="7.5" fill="#a04010">{dp.note}</text>
          </g>
        ))}
        <text x={700} y={192} fontSize="9" fontWeight="700" fill="#7a2e00">Example items:</text>
      </svg>
      <div style={S.dCaption}>The criticality matrix provides rapid visual prioritisation — items in red cells (Catastrophic + Frequent/Probable) must be eliminated or mitigated before design release regardless of calculated RPN</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function FMEAPage() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Failure Mode and Effects Analysis (FMEA)</h1>

      <p style={S.p}>
        <strong>Failure Mode and Effects Analysis (FMEA)</strong> is a systematic, proactive technique for
        identifying all the ways a system, component, or process might fail, analysing the effect of each
        failure on system performance and safety, and ranking failures by their risk so that the design or
        process team can prioritise corrective actions before failures reach the customer. Originally
        developed for US military systems (MIL-P-1629, 1949) and formalised for automotive manufacturing
        by the AIAG, FMEA is today mandated by IATF 16949, ISO 26262, ISO 13849, and AS9100.
      </p>

      <h2 style={S.h2}>Process Flow — The Seven-Step Methodology</h2>
      <p style={S.p}>
        FMEA follows a structured seven-step loop. The team iterates until the RPN (or Action Priority)
        for every identified failure mode is reduced below the project threshold, or until it is
        documented that the residual risk is acceptable with existing controls.
      </p>
      <DiagramProcessFlow />
      <Table2 rows={[
        ['Step 1 — Scope','Define the system boundary: which assembly, sub-system, or process is covered\nIdentify interfaces (what connects to this system) and assumptions\nDocument the analysis intent: safety, reliability, quality, or process control'],
        ['Step 2 — Functions','State what each component or process step must do (its function)\nUse verb-noun format: "transmit torque of 500 N·m", "seal fluid at 200 bar"\nOne component can have multiple functions — each must be analysed separately'],
        ['Step 3 — Failure Modes','Failure mode = the way the function fails: "transmits too little torque", "fails to seal"\nCategories: no function, partial function, degraded function, intermittent, unintended function\nBrainstorm using: past field data, warranty reports, similar product history, expert knowledge'],
        ['Step 4 — Effects','Effect on the customer (internal or external) if the failure mode occurs\nConsider: immediate effect (on sub-system) and end effect (on vehicle/machine/user)\nEffects drive the Severity (S) rating — always evaluate the worst credible outcome'],
        ['Step 5 — Causes','Root cause of the failure mode — why it could occur\nMust be specific and actionable (not "human error" but "torque value missing from WI")\nMultiple causes per failure mode are common — each needs independent O and D ratings'],
        ['Step 6 — S, O, D','Severity (S): 1–10 — impact on customer/system if failure occurs\nOccurrence (O): 1–10 — likelihood of cause occurring (linked to Cpk/process data)\nDetection (D): 1–10 — ability of current controls to detect the cause before reaching customer'],
        ['Step 7 — RPN & Actions','RPN = S × O × D  (range: 1–1000)\nPrioritise by RPN threshold (typically 100–200) AND by any item with S≥9 or O≥8\nDocument: recommended action, responsible person, target date, and re-assessed RPN after action'],
      ]}/>

      <h2 style={S.h2}>RPN Heat Map — Severity × Occurrence</h2>
      <p style={S.p}>
        The Risk Priority Number combines three independent dimensions. This heat map fixes Detection at 5
        (moderate) to visualise the two-dimensional S×O interaction. High Severity failures demand
        attention even at low Occurrence — never rely on Detection alone to manage a high-Severity risk.
      </p>
      <DiagramRPNMatrix />
      <Table2 rows={[
        ['RPN formula','RPN = S × O × D  (integer product, range 1–1000)\nThree equal-weight factors: changing any one by the same ratio has the same RPN impact\nCritique: two different S×O×D combinations can produce the same RPN with very different risk profiles'],
        ['RPN limitations','RPN is not linearly proportional to risk — S=10,O=1,D=1 (RPN=10) is often more dangerous than S=2,O=5,D=4 (RPN=40)\nDifferent teams rate identically described failure modes differently (subjectivity)\nAIAG-VDA 2019 introduced Action Priority (AP) system to supplement or replace standalone RPN'],
        ['Severity independence','S≥9 (safety/regulatory) must ALWAYS trigger corrective action regardless of O and D\nS=10 items should be redesigned to eliminate the failure mode or reduce maximum harm\nDetection controls (D) should never be the primary mitigation for high-Severity failures'],
        ['Occurrence and Cpk','Occurrence rating is ideally linked to statistical process data: Cpk, PPM, SPC charts\nCpk ≥ 1.33 → O ≈ 3 (1 in 15,000) · Cpk ≥ 1.67 → O ≈ 1 (almost impossible)\nNew processes without data: use expert engineering judgement + comparison to similar processes'],
        ['Detection controls','D=1: prevention design (failure physically impossible) — most powerful\nD=2–3: mistake-proofing (poka-yoke) catches failure before next operation\nD=4–6: 100% in-process inspection or testing\nD=7–9: sampling inspection or end-of-line test\nD=10: no control — failure reaches customer undetected'],
      ]}/>

      <h2 style={S.h2}>Rating Scales — S, O, D</h2>
      <p style={S.p}>
        Rating consistency across the team is the single biggest practical challenge in FMEA execution.
        The tables below follow AIAG FMEA-4 guidance. Each organisation should define specific examples
        for each rating level calibrated to their product and customer expectations.
      </p>
      <DiagramRatingScales />
      <Table2 rows={[
        ['Severity calibration','S=9–10: safety and legal non-compliance — non-negotiable action required\nS=7–8: primary function lost — customer will likely return or complain formally\nS=5–6: secondary function affected — customer notices significant performance loss\nS=1–4: cosmetic or minor — may go unnoticed or cause minor inconvenience'],
        ['Occurrence calibration','O should be rated for the cause, not the failure mode directly\nBase on process capability data where available (Cpk, SPC charts, PPM data)\nFor new processes: use similarity to existing processes and engineering judgement\nO=10 means the cause is almost certain to occur — requires immediate process redesign'],
        ['Detection calibration','D should be rated for the current control plan, not hoped-for future controls\nD=1 requires design-level prevention (failure is physically prevented, not just caught)\nBe conservative: rate D based on what is currently implemented, not what is planned\nD=10 means no test, no inspection, no check of any kind exists for this cause'],
        ['Cross-functional teams','FMEA ratings are more accurate and accepted when made by cross-functional teams\nTypical team: design, manufacturing, quality, reliability, field service, supplier\n"Champion" facilitator ensures consistent rating criteria are applied throughout\nReview cadence: at each design review gate and after any engineering change'],
      ]}/>

      <h2 style={S.h2}>Failure Mode Taxonomy — Function → Mode → Cause → Effect</h2>
      <p style={S.p}>
        Systematically mapping failure modes requires moving through four levels: what the component
        must do (function), how that function can fail (failure mode), why the failure occurs (cause),
        and what happens as a result (effect). The hydraulic pump example below illustrates one branch
        of this tree for a single sub-system.
      </p>
      <DiagramFailureTree />
      <Table2 rows={[
        ['Failure mode categories','No function: fails to perform at all ("no flow", "no torque")\nPartial / degraded: performs below requirement ("reduced flow", "high torque ripple")\nIntermittent: functions sometimes only ("intermittent electrical contact")\nUnintended function: performs something not required ("valve opens when closed command sent")\nOver-function: exceeds requirement ("excess pressure beyond relief setting")'],
        ['Cause specificity','Causes must be specific enough to drive corrective action\nBAD: "design error" · GOOD: "insufficient keyway radius creates notch effect stress ×2.4"\nBAD: "material failure" · GOOD: "material hardness below minimum HRC 58 due to quench rate"\nOne failure mode can have 3–10 distinct causes, each requiring its own O and D rating'],
        ['Effects chain','Immediate effect: on the sub-system where the failure occurs\nHigher-level effect: on the next assembly or system\nEnd effect: on the vehicle/machine/user — this is what drives the Severity rating\nExample: worn pump vane → reduced flow (sub-system) → slow cylinder (system) → missed production target (end)'],
        ['Failure modes vs symptoms','Failure modes are functional states ("shaft fractured"), not symptoms ("vibration")\nSymptoms are effects, not causes — distinguish carefully\nA single failure mode (shaft fracture) can have multiple effects depending on operating conditions\nDocument all plausible worst-case effects, then rate S on the most severe'],
      ]}/>

      <h2 style={S.h2}>Sample FMEA Worksheet</h2>
      <p style={S.p}>
        The FMEA worksheet is the primary deliverable. Each row captures one cause-effect pair for one
        failure mode. The worksheet below shows six entries across three mechanical assemblies, with
        before-action RPNs and the recommended corrective actions.
      </p>
      <DiagramWorksheet />
      <Table2 rows={[
        ['Worksheet columns','Item/Function → Failure Mode → Effect(s) → S → Cause(s) → O → Current Controls → D → RPN → Recommended Action → Responsible/Target Date → Revised S/O/D/RPN after action\nEach cause-effect combination occupies its own row\nMultiple causes per failure mode: each row has its own O and D rating'],
        ['Current controls','List ALL existing controls that currently prevent the cause or detect the failure\nPrevention controls: those that reduce Occurrence (O) — process specifications, design standards\nDetection controls: those that find the failure before customer delivery — inspection, test, SPC\nDo not include planned or future controls — only what exists today'],
        ['Recommended actions','Actions must reduce RPN: lower O (process improvement), lower D (better detection), or lower S (redesign)\nFor S≥9: action must redesign to either eliminate the failure mode or reduce maximum harm\nFor O≥8: must improve process capability — inspection/detection alone is insufficient\nFor D≥8: add inspection, poka-yoke, or automatic test'],
        ['Action completeness','Every recommended action needs: clear description, responsible engineer name, target date\nAfter action: verify implementation, re-rate O and D, recalculate RPN\nIf no action is taken: document the rationale why current controls are adequate\nFMEA is not complete until all high-priority items are closed or formally accepted'],
      ]}/>

      <h2 style={S.h2}>DFMEA vs PFMEA vs FMEA-MSR</h2>
      <DiagramTypesComparison />
      <Table2 rows={[
        ['DFMEA vs PFMEA','DFMEA asks: "If the design is correct, could it still fail?" — focus on inherent design weaknesses\nPFMEA asks: "If the design is correct, could the manufacturing process build it wrong?" — focus on process variations\nA key distinction: the DFMEA Severity rating assumes the product is manufactured exactly to specification\nThe PFMEA Occurrence rating reflects actual manufacturing process capability, not design intent'],
        ['AIAG-VDA 2019 revision','Major change: 6-step approach (structure analysis, function analysis, failure analysis, risk analysis, optimisation, documentation)\nNew: Action Priority (H/M/L) replaces standalone RPN threshold — more nuanced than a single cutoff number\nNew: Focus element (unique to each step) ensures team analyses at the correct system level\nMandatory fields include: customer target, family FMEA reference, and AP documentation'],
        ['FMEA-MSR','Monitoring & System Response FMEA — third document in AIAG-VDA 2019 trilogy\nAddresses failure modes that are dormant or detectable only by the vehicle E/E system\nLinks FMEA to diagnostic coverage (DC), safety goals, and FMEDA\nRequired for ISO 26262 functional safety and increasingly for ISO 21448 (SOTIF)'],
        ['Interface FMEA','Analyses failure at the boundary between two sub-systems\nCaptures design responsibility gaps ("this is your boundary, not mine")\nEssential for complex assemblies where no single engineer owns the interface\nInput to interface control documents (ICD) and interface test plans'],
        ['Software FMEA','Applies the FMEA method to software modules and state machines\nFailure modes: wrong output, no output, output at wrong time, output in wrong format\nOften combined with Fault Tree Analysis (FTA) for software/hardware interaction\nUsed in IEC 62061, ISO 26262 Part 6, and DO-178C safety frameworks'],
      ]}/>

      <h2 style={S.h2}>RPN Before vs. After Corrective Actions</h2>
      <DiagramRPNWaterfall />
      <Table2 rows={[
        ['Corrective action types','Design change (reduces S or O): material change, geometry change, tolerance change\nProcess change (reduces O): new tooling, fixture, SPC chart, process parameter control\nDetection improvement (reduces D): 100% inspection, CMM, automated vision, poka-yoke\nPrevention (D=1): design change makes failure physically impossible — most powerful'],
        ['Targeting Occurrence','O is most effectively reduced by improving process capability (higher Cpk)\nCpk improvement: tighter tolerances, better tooling, SPC monitoring, reduced setup variation\nFor new designs: use robust design methods (Taguchi, DOE) to reduce sensitivity to variation\nO reduction is preferred over D improvement — catching defects is never as good as not making them'],
        ['Targeting Detection','D is reduced by adding or improving detection controls in the control plan\nError-proofing (poka-yoke) levels: contact method, fixed-value method, motion-step method\nAutomated inspection (vision systems, laser measurement) is more reliable than manual inspection\nD should never be the only mitigation for S=9 or S=10 failure modes'],
        ['Re-assessment timing','Re-rate O and D after the corrective action has been IMPLEMENTED AND VERIFIED, not just planned\nVerification methods: capability study (new Cpk), pilot run data, inspection escape rate\nRevised RPN should be formally recorded in the FMEA with the action completion date\nFMEA closure: all H/M priority items addressed; residual risk formally accepted by responsible engineer'],
      ]}/>

      <h2 style={S.h2}>FMEA in the V-Model and Development Timeline</h2>
      <DiagramVModel />
      <Table2 rows={[
        ['FMEA timing','Earliest possible start maximises design freedom to make changes\nConcept FMEA: before any geometry is fixed — maximum leverage to reduce risk\nPreliminary DFMEA: at system design stage — define key characteristics (KCs)\nDetailed DFMEA: during component design — drives drawing notes, tolerances, DVP&R tests'],
        ['Design reviews','FMEA should be reviewed at every formal design review gate\nGate criteria: no open S≥9 or AP=H items; all actions with responsible owner and target date\nPDR (Preliminary Design Review): DFMEA structure complete, failure modes identified\nCDR (Critical Design Review): all O and D ratings assigned, actions for RPN>threshold defined\nFDR (Final Design Review): all actions closed or formally accepted with rationale'],
        ['Living document','FMEA is never "finished" — it should be updated when:\n  • Engineering change notice (ECN) modifies a component or process\n  • Field failure or warranty claim identifies a new failure mode\n  • Supplier changes a manufacturing process or material\n  • New regulatory requirement changes severity of a failure effect'],
        ['Lessons learned','FMEA history captures institutional knowledge — failure modes discovered once should never recur\nFamily FMEA: build from previous product FMEA as starting template for new products\nFailure mode library: maintain a searchable database of all failure modes, causes, and proven controls\nPost-launch: compare predicted failure modes to actual field data; update FMEA to match reality'],
      ]}/>

      <h2 style={S.h2}>MIL-STD-1629A Criticality Matrix</h2>
      <p style={S.p}>
        MIL-STD-1629A (and its successor MIL-STD-1629) adds a quantitative criticality number (Cr)
        calculated from failure rate data, and plots failure modes on a criticality matrix. This
        approach is used in aerospace, defence, and nuclear industries where failure rate data is
        available from MIL-HDBK-217 or reliability prediction.
      </p>
      <DiagramCriticalityMatrix />
      <Table2 rows={[
        ['Criticality number (Cr)','Cr = β × α × λp × t\nβ = conditional probability of failure effect (0–1)\nα = failure mode ratio (fraction of λp for this mode)\nλp = part failure rate (failures per 10⁶ hours, from MIL-HDBK-217)\nt = mission duration (hours)'],
        ['MIL-STD severity categories','Category I (Catastrophic): death, loss of system — no acceptable probability level\nCategory II (Critical): severe injury, major system damage — requires mitigation to acceptable level\nCategory III (Marginal): minor injury, significant degradation — mitigate where practical\nCategory IV (Negligible): no injury, minor non-operational damage — document only'],
        ['Probability level definitions','Level A (Frequent): occurs one or more times during system life (Pr > 0.20)\nLevel B (Probable): likely to occur several times (0.10 < Pr < 0.20)\nLevel C (Occasional): likely to occur sometime (0.01 < Pr < 0.10)\nLevel D (Remote): unlikely to occur but possible (0.001 < Pr < 0.01)\nLevel E (Improbable): so unlikely it can be assumed it will not occur (Pr < 0.001)'],
        ['FMEA vs FMECA','FMEA: qualitative — identifies modes, effects, and ranks by RPN\nFMECA (Failure Mode, Effects, and Criticality Analysis): adds quantitative criticality using failure rate data\nFMECA is required by MIL-STD-1629A, DO-178C, and IEC 60812 for safety-critical systems\nAIAG automotive FMEA is qualitative; aerospace AS9100 often requires FMECA'],
      ]}/>

      <h2 style={S.h2}>Integration with Other Quality Tools</h2>
      <Table2 rows={[
        ['FMEA + Control Plan','DFMEA special characteristics → PFMEA → Control Plan (CP)\nCP specifies: product characteristic, process characteristic, control method, sample size, reaction plan\nAll FMEA "current controls" should appear explicitly in the control plan\nKey Characteristics (KCs) from DFMEA drive the CP inspection intensity and Cpk requirements'],
        ['FMEA + FTA','Fault Tree Analysis (FTA): top-down deductive — starts from undesired top event, works down\nFMEA: bottom-up inductive — starts from component failures, works up to system effects\nBoth are complementary — FTA ensures FMEA has not missed a combination failure mode\nFor safety-critical systems (ISO 26262, IEC 61508): both FMEA and FTA are typically required'],
        ['FMEA + DVP&R','Design Verification Plan & Report: test program derived from DFMEA\nFMEA failure modes → test cases that can detect or confirm each failure mode\nFailure modes with D≥7 (hard to detect) need specific DVP test coverage\nAfter test: if failure mode is observed in DVP, DFMEA must be updated with revised design'],
        ['FMEA + SPC','Process FMEA high-O causes → Statistical Process Control (SPC) chart additions\nSPC provides real-time O monitoring: control chart detects process shifts before defects occur\nFMEA review should include SPC data: if a cause has O=6 but SPC shows Cpk=1.60, revise O downward\nNew out-of-control events on SPC should trigger PFMEA review of affected failure modes'],
        ['FMEA + 8D / CAPA','When a field failure occurs: 8D root cause analysis → update FMEA\nFMEA is a living document — every corrective action (CAPA) should trace back to an FMEA row\n8D Problem Statement: should identify which FMEA failure mode was missed or under-rated\nLesson learned: update family FMEA so the failure mode is captured for future products'],
        ['FMEA + Reliability','FMEA provides failure mode list for reliability block diagrams and reliability allocation\nFMEA + Reliability Prediction (MIL-HDBK-217, FIDES): quantifies O rating with actual failure rates\nReliability targets drive allowed Occurrence ratings: if MTBF target = 50,000 h, maximum O per mode is limited\nDegradation analysis (Weibull): connects FMEA failure modes to field life distribution data'],
      ]}/>

      <h2 style={S.h2}>Common FMEA Mistakes &amp; Best Practices</h2>
      <Table2 rows={[
        ['Too late in the process','Starting FMEA after the design is frozen provides minimal value — changes are expensive and resisted\nBest practice: begin DFMEA at the concept stage when design freedom is maximum\nRule of thumb: cost of change increases 10× at each phase (concept → design → prototype → production)'],
        ['Symptom vs failure mode','Listing symptoms as failure modes (e.g., "vibration" instead of "bearing race fatigue")\nSymptoms belong in the Effects column, not the Failure Mode column\nCorrect failure mode format: verb+noun describing the functional failure, not the observable symptom'],
        ['Non-specific causes','Causes like "human error", "wrong material", "design error" are not actionable\nSpecific cause: "operator applies incorrect torque because torque value is not visible on work instruction"\nThe more specific the cause, the more targeted and effective the corrective action'],
        ['Gaming the RPN','Inflating D ratings to achieve low RPN without improving actual design or process\n"We can detect this in final test" → D=4, RPN falls below threshold — but risk is unchanged\nBest practice: detection controls should be validated; operators should demonstrate detection capability'],
        ['Single discipline team','FMEA conducted by one engineer in isolation misses ~40–60% of failure modes\nDiverse team sees different aspects: manufacturing sees buildability issues, service sees wear patterns\nFacilitator role: ensure quieter team members contribute; challenge ratings that seem too optimistic'],
        ['No action follow-through','FMEA lists hundreds of actions with no owner, no date, no follow-up\nBest practice: FMEA action register with formal monthly review; escalate overdue H/M priority items\nFMEA without closed actions is a compliance exercise — it provides no actual risk reduction'],
      ]}/>

      <InfoBox title="Key FMEA Formulae &amp; Thresholds" items={[
        "RPN = S × O × D   (range: 1–1,000; each factor 1–10)",
        "Criticality Number (MIL-1629A):  Cr = β × α × λp × t",
        "Action Priority (AIAG-VDA 2019):  AP = H / M / L  (lookup table, not product)",
        "Common RPN threshold:  ≥ 100 document & monitor  ·  ≥ 200 corrective action required",
        "Severity rule:  S ≥ 9  →  action mandatory regardless of RPN",
        "Occurrence rule:  O ≥ 8  →  process redesign required; detection alone is insufficient",
        "Process capability link:  Cpk ≥ 1.33 → O ≈ 3  ·  Cpk ≥ 1.67 → O ≈ 1",
        "Fault coverage:  D = 1 (prevention)  >  D = 2–3 (poka-yoke)  >  D = 4–6 (100% inspect)  >  D = 7–9 (sample)",
        "FMEA update triggers:  ECN / field failure / supplier change / regulatory revision",
        "FMECA:  Cr_total = Σ(Cr_i) across all failure modes  →  higher Cr = higher criticality priority",
      ]}/>
    </div>
  );
}